export function resolveCategoryId(form, categories = []) {
  if (form?.categoryId != null && form.categoryId !== '') {
    const parsed = Number(form.categoryId)
    if (parsed > 0) {
      return parsed
    }
  }

  const label = String(form?.category || '').trim()
  if (label && Array.isArray(categories) && categories.length) {
    const match = categories.find(
      item => String(item.text || item.name || '').toLowerCase() === label.toLowerCase(),
    )
    if (match?.value) {
      return Number(match.value)
    }
    if (match?.id) {
      return Number(match.id)
    }
  }

  return null
}

export function combineDateTime(date, time) {
  if (!date) return null
  const parts = String(time || '00:00').split(':')
  const hours = (parts[0] || '00').padStart(2, '0')
  const minutes = (parts[1] || '00').padStart(2, '0')
  return `${date} ${hours}:${minutes}:00`
}

/** Local date + time from the schedule form → ISO 8601 UTC for the API. */
export function combineDateTimeUtc(date, time) {
  if (!date) return null
  const parts = String(time || '00:00').split(':')
  const hours = Number(parts[0] || 0)
  const minutes = Number(parts[1] || 0)
  const [year, month, day] = String(date).split('-').map(Number)
  if (!year || !month || !day) return null

  const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0)
  if (Number.isNaN(localDate.getTime())) return null

  return localDate.toISOString()
}

/** Existing media URLs from ad detail API (`ads_image`, `media`, or single `image`). */
export function resolveExistingAdMediaUrls(adDetail = {}) {
  const adsImages = Array.isArray(adDetail.ads_image)
    ? adDetail.ads_image.map(url => String(url || '').trim()).filter(Boolean)
    : []
  if (adsImages.length) {
    return [...new Set(adsImages)]
  }

  const media = Array.isArray(adDetail.media)
    ? adDetail.media.map(url => String(url || '').trim()).filter(Boolean)
    : []
  if (media.length) {
    return [...new Set(media)]
  }

  const singleImage = String(adDetail.image || '').trim()
  return singleImage ? [singleImage] : []
}

export function buildAdApiPayload({
  form,
  schedule,
  mediaUrls,
  categories = [],
}) {
  const urls = Array.isArray(mediaUrls) ? mediaUrls.filter(Boolean) : []
  const categoryId = resolveCategoryId(form, categories)
  const secondaryRaw = form?.secondaryCategoryId ?? form?.secondary_category_id
  const secondaryCategoryId = secondaryRaw != null && secondaryRaw !== ''
    ? Number(secondaryRaw)
    : null
  const minAgeRaw = form?.minAge ?? form?.min_age
  const minAge = minAgeRaw === null || minAgeRaw === undefined || minAgeRaw === ''
    ? null
    : Number(minAgeRaw)

  const brandRaw = form?.brandId ?? form?.brand_id
  const brandId = brandRaw != null && brandRaw !== '' ? Number(brandRaw) : null

  const payload = {
    title: String(form.title || '').trim(),
    description: String(form.description || '').trim(),
    ad_type: String(form.adType || 'Image').toLowerCase(),
    image: urls[0] || '',
    media: urls,
    cta_text: String(form.ctaText || '').trim(),
    cta_link: String(form.destinationUrl || '').trim(),
    publish_at: combineDateTimeUtc(schedule.startDate, schedule.startTime),
    expire_date: combineDateTimeUtc(schedule.endDate, schedule.endTime),
  }

  if (brandId != null && Number.isFinite(brandId) && brandId > 0) {
    payload.brand_id = brandId
  }

  if (categoryId != null) {
    payload.category_id = categoryId
  }

  if (secondaryCategoryId != null && secondaryCategoryId > 0) {
    payload.secondary_category_id = secondaryCategoryId
  } else {
    payload.secondary_category_id = null
  }

  if (minAge != null && Number.isFinite(minAge)) {
    payload.min_age = minAge
  }

  const gender = String(form?.targetGender ?? form?.target_gender ?? 'any').trim().toLowerCase()
  payload.target_gender = gender && gender !== 'any' ? gender : 'any'

  const ageMinRaw = form?.targetAgeMin ?? form?.target_age_min
  const ageMaxRaw = form?.targetAgeMax ?? form?.target_age_max
  const ageMin = ageMinRaw === null || ageMinRaw === undefined || ageMinRaw === ''
    ? null
    : Number(ageMinRaw)
  const ageMax = ageMaxRaw === null || ageMaxRaw === undefined || ageMaxRaw === ''
    ? null
    : Number(ageMaxRaw)

  payload.target_age_min = ageMin != null && Number.isFinite(ageMin) ? ageMin : null
  payload.target_age_max = ageMax != null && Number.isFinite(ageMax) ? ageMax : null

  return payload
}

export function normalizeAdStatusKey(status) {
  return String(status || '').replace(/\s+/g, '_').toLowerCase()
}

export function validateDestinationUrl(url) {
  const value = String(url || '').trim()
  if (!value) {
    return 'Destination URL is required'
  }
  if (/\s/.test(value)) {
    return 'Destination URL cannot contain spaces'
  }
  try {
    const parsed = new URL(value)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'Destination URL must start with http:// or https://'
    }
    if (!parsed.hostname) {
      return 'Please enter a valid URL (e.g. https://example.com)'
    }
  } catch {
    return 'Please enter a valid URL (e.g. https://example.com)'
  }
  return null
}

export function validateScheduleRange(schedule) {
  const start = combineDateTime(schedule.startDate, schedule.startTime)
  const end = combineDateTime(schedule.endDate, schedule.endTime)
  if (!start || !end) return null
  if (new Date(end).getTime() <= new Date(start).getTime()) {
    return 'End date and time must be after start date and time'
  }
  return null
}

export function formatCompactCount(value) {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(Number(value || 0))
}

export function formatIsoDateLabel(value) {
  if (!value) return 'MM/DD/YYYY'
  const [year, month, day] = String(value).split('-')
  if (!year || !month || !day) return 'MM/DD/YYYY'
  return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`
}

export function formatDisplayDate(value) {
  if (!value) return '-'
  const raw = String(value).trim()
  if (!raw) return '-'

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) {
    const [, year, month, day] = isoMatch
    return `${month}/${day}/${year}`
  }

  const parsed = new Date(raw)
  if (!Number.isNaN(parsed.getTime())) {
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const day = String(parsed.getDate()).padStart(2, '0')
    const year = parsed.getFullYear()
    return `${month}/${day}/${year}`
  }

  return raw
}

export function formatDisplayDateTime(value) {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return formatDisplayDate(value)
  return `${formatDisplayDate(parsed.toISOString())} ${parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

export function parseApiDateTime(iso) {
  if (!iso) return { date: '', time: '' }
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return { date: '', time: '' }
  const year = parsed.getFullYear()
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  const hours = String(parsed.getHours()).padStart(2, '0')
  const minutes = String(parsed.getMinutes()).padStart(2, '0')
  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}`,
  }
}

export function resolveCategoryLabel(categoryName, categories = []) {
  const normalized = String(categoryName || '').trim().toLowerCase()
  if (normalized && Array.isArray(categories) && categories.length) {
    const match = categories.find(
      item => String(item.text || item.name || '').toLowerCase() === normalized,
    )
    if (match?.value) {
      return Number(match.value)
    }
    if (match?.id) {
      return Number(match.id)
    }
  }

  return null
}

export function extractApiErrorMessage(error, fallback = 'Something went wrong') {
  const status = error?.response?.status
  if (status === 413) {
    return 'Upload is too large. Images must be 5MB or less; videos must be 50MB or less.'
  }

  const data = error?.response?.data
  if (data?.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors).flat().find(Boolean)
    if (first) return String(first)
  }
  if (data?.message) return String(data.message)

  if (!error?.response && error?.request) {
    return 'Upload failed. The file may be too large for the server, or the connection was interrupted.'
  }

  if (error?.message) return String(error.message)
  return fallback
}

export function buildClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/** @returns {Array<{ value: number, text: string }>} */
export function applyCategoryOptions(categories) {
  return (Array.isArray(categories) ? categories : [])
    .map(item => ({
      value: Number(item.id),
      text: String(item.name || item.text || '').trim(),
    }))
    .filter(item => item.value > 0 && item.text)
}

/** Fallback when categories API is unavailable. */
export function getCategoryOptionLabels() {
  return []
}

/** Matches API AdMediaFormats::MAX_KILOBYTES (50MB). */
export const AD_MEDIA_MAX_BYTES = 50 * 1024 * 1024

export const AD_MEDIA_MAX_MB = 50

export function isAdMediaWithinSizeLimit(file, maxBytes = AD_MEDIA_MAX_BYTES) {
  if (!file || typeof file.size !== 'number') return true
  return file.size <= maxBytes
}

export const AD_IMAGE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif', 'heic', 'heif', 'tif', 'tiff', 'ico',
]

export const AD_VIDEO_EXTENSIONS = [
  'mp4', 'm4v', 'mov', 'webm', 'avi', 'mkv', 'mpeg', 'mpg', '3gp', 'ogv', 'wmv',
]

export const AD_IMAGE_FORMAT_HINT = 'JPG, PNG, GIF, WebP, SVG, BMP, AVIF, HEIC, or TIFF.'

export const AD_VIDEO_FORMAT_HINT = 'MP4, MOV, WebM, M4V, AVI, MKV, MPEG, 3GP, OGV, or WMV.'

export const AD_IMAGE_FORMAT_ERROR_MESSAGE = 'Please upload a valid image (JPG, PNG, GIF, WebP, SVG, BMP, AVIF, HEIC, or TIFF).'

export const AD_VIDEO_FORMAT_ERROR_MESSAGE = 'Please upload a valid video (MP4, MOV, WebM, M4V, AVI, MKV, MPEG, 3GP, OGV, or WMV).'

const GENERIC_FILE_TYPES = new Set(['', 'application/octet-stream', 'binary/octet-stream', 'text/plain'])

function fileExtension(file) {
  return String(file?.name || '').split('.').pop().toLowerCase()
}

function fileMime(file) {
  return String(file?.type || '').toLowerCase()
}

export function isAllowedAdImageFile(file) {
  if (!file) return false
  const ext = fileExtension(file)
  const mime = fileMime(file)
  if (!AD_IMAGE_EXTENSIONS.includes(ext)) {
    return ext === '' && mime.startsWith('image/')
  }
  if (GENERIC_FILE_TYPES.has(mime)) return true
  if (ext === 'svg') {
    return mime.startsWith('image/') || mime.includes('xml') || mime.startsWith('text/')
  }
  return mime.startsWith('image/')
}

export function isAllowedAdVideoFile(file) {
  if (!file) return false
  const ext = fileExtension(file)
  const mime = fileMime(file)
  if (!AD_VIDEO_EXTENSIONS.includes(ext)) {
    return ext === '' && mime.startsWith('video/')
  }
  if (GENERIC_FILE_TYPES.has(mime)) return true
  return mime.startsWith('video/') || mime === 'application/ogg'
}

export function isAdVideoFile(file) {
  const ext = fileExtension(file)
  const mime = fileMime(file)
  return AD_VIDEO_EXTENSIONS.includes(ext) || mime.startsWith('video/')
}

export function isVideoMediaUrl(url) {
  const path = String(url || '').split('?')[0].split('#')[0].toLowerCase()
  return AD_VIDEO_EXTENSIONS.some(ext => path.endsWith(`.${ext}`))
}

export function isAllowedPngJpegAdImageFile(file) {
  return isAllowedAdImageFile(file)
}

export function getAdMediaFormatError(files, adType) {
  const list = Array.isArray(files) ? files : []
  const videoAd = String(adType || '').toLowerCase() === 'video'
  const checker = videoAd ? isAllowedAdVideoFile : isAllowedAdImageFile
  const hasInvalidFile = list.some(file => (
    typeof File !== 'undefined'
    && file instanceof File
    && !checker(file)
  ))
  if (!hasInvalidFile) return ''
  return videoAd ? AD_VIDEO_FORMAT_ERROR_MESSAGE : AD_IMAGE_FORMAT_ERROR_MESSAGE
}

export function getAdImageFormatError(files) {
  return getAdMediaFormatError(files, 'image')
}
